// frontend/src/components/surveys/surveyUtils.js
// Survey utility functions for menu visibility and availability checks
// Separates admin menu visibility from end-user survey visibility

import surveyService from '../../services/surveyService';
import axios from '../../axiosConfig';
import { hasRoleLevel } from '../../utils/roleUtils';

/**
 * Check if Survey Management menu should be visible (ADMIN MENU)
 *
 * Visibility Rules for Admin Menu:
 * - Rule 1: Both survey types globally inactive (is_active = 0) → Menu invisible
 * - Rule 2: At least one survey type globally active (is_active = 1) + user has required_role → Menu visible
 * - Rule 3: Does NOT apply (entity preferences don't affect admin menu visibility)
 *
 * @param {number} entityId - Entity ID to check visibility for
 * @returns {Promise<boolean>} True if user should see Survey Management menu
 */
export const shouldShowSurveyManagementMenu = async (entityId) => {
  if (!entityId) {
    return false;
  }

  try {
    // Check if user has management permissions (canManage = Rules 1 & 2 only)
    const visibility = await surveyService.checkMenuVisibility(entityId);
    return visibility.canManage || false;
  } catch (error) {
    console.error('Error checking survey management menu visibility:', error);

    // Fallback: Try to determine visibility client-side
    try {
      // Get preference definitions to check global is_active
      const prefDefsResponse = await axios.get('/api/preferences/definitions');
      const definitions = prefDefsResponse.data.data || [];

      // Check if any survey type is globally active
      const serviceDef = definitions.find(d => d.preference_key === 'service_survey_enabled');
      const systemDef = definitions.find(d => d.preference_key === 'system_survey_enabled');

      // Rule 1: If both are inactive globally, menu should not be visible
      if ((!serviceDef || !serviceDef.is_active) && (!systemDef || !systemDef.is_active)) {
        return false;
      }

      // Rule 2: At least one is globally active, check if user has required role
      const userRole = localStorage.getItem('role');

      // Check if user has required role for any active survey type
      if (serviceDef?.is_active) {
        const serviceRequiredRole = serviceDef.required_role || 'admin';
        if (hasRoleLevel(userRole, serviceRequiredRole)) {
          return true;
        }
      }
      if (systemDef?.is_active) {
        const systemRequiredRole = systemDef.required_role || 'admin';
        if (hasRoleLevel(userRole, systemRequiredRole)) {
          return true;
        }
      }

      // User doesn't have required role for any active survey type
      return false;
    } catch (fallbackError) {
      console.error('Error in fallback management menu visibility check:', fallbackError);
      return false;
    }
  }
};

/**
 * Check if surveys should be shown to END USERS (for taking surveys)
 *
 * Visibility Rules for End User Surveys:
 * - Rule 1: Survey type globally inactive (is_active = 0) → Survey invisible
 * - Rule 2: Survey type globally active (is_active = 1) + entity preference enabled → Survey visible
 * - NO role checking for end users
 *
 * @param {number} entityId - Entity ID to check visibility for
 * @returns {Promise<boolean>} True if surveys should be shown to end users
 */
export const shouldShowSurveysForUser = async (entityId) => {
  if (!entityId) {
    return false;
  }

  try {
    // Check if any survey is visible to users (visible = Rules 1 & simplified 3)
    const visibility = await surveyService.checkMenuVisibility(entityId);
    return visibility.visible || false;
  } catch (error) {
    console.error('Error checking end user survey visibility:', error);

    // Fallback: Try to determine visibility client-side
    try {
      // Get preference definitions to check global is_active
      const prefDefsResponse = await axios.get('/api/preferences/definitions');
      const definitions = prefDefsResponse.data.data || [];

      // Check if any survey type is globally active
      const serviceDef = definitions.find(d => d.preference_key === 'service_survey_enabled');
      const systemDef = definitions.find(d => d.preference_key === 'system_survey_enabled');

      // Get entity settings (use correct endpoint for entity-level preferences)
      const entitySettingsResponse = await axios.get(`/api/settings/entity/${entityId}`);
      const entitySettings = entitySettingsResponse.data.data || {};

      // Check if any globally active survey is also enabled for this entity
      if (serviceDef?.is_active && (entitySettings.service_survey_enabled === true || entitySettings.service_survey_enabled === 'true')) {
        return true;
      }
      if (systemDef?.is_active && (entitySettings.system_survey_enabled === true || entitySettings.system_survey_enabled === 'true')) {
        return true;
      }

      return false;
    } catch (fallbackError) {
      console.error('Error in fallback end user survey visibility check:', fallbackError);
      return false;
    }
  }
};

/**
 * DEPRECATED: Use shouldShowSurveyManagementMenu or shouldShowSurveysForUser instead
 * @deprecated
 */
export const hasAnySurveyEnabled = shouldShowSurveyManagementMenu;

/**
 * Synchronous version for backward compatibility
 * This is a simplified check that doesn't implement full three-tier rules
 * @param {Object} systemSettings - System settings from usePreferences
 * @returns {boolean} True if any survey is enabled in settings
 */
export const hasAnySurveyEnabledSync = (systemSettings) => {
  if (!systemSettings) {
    return false;
  }

  // Simple check - just check if surveys are enabled in entity settings
  // This doesn't check global is_active or required roles
  const surveyEnabled = 
    systemSettings.service_survey_enabled === true || 
    systemSettings.service_survey_enabled === 'true' ||
    systemSettings.system_survey_enabled === true || 
    systemSettings.system_survey_enabled === 'true';
  
  return surveyEnabled;
};

/**
 * Get available survey types for current user
 * @param {number} entityId - Entity ID
 * @returns {Promise<Object>} Available survey types with permissions
 */
export const getAvailableSurveyTypes = async (entityId) => {
  if (!entityId) {
    return {
      hasAny: false,
      service: false,
      system: false
    };
  }

  try {
    const visibility = await surveyService.checkMenuVisibility(entityId);
    
    return {
      service: visibility.channels?.service || false,
      system: visibility.channels?.system || false,
      hasAny: visibility.visible || false,
      canManage: visibility.canManage || false
    };
  } catch (error) {
    console.error('Error getting available survey types:', error);
    return {
      hasAny: false,
      service: false,
      system: false
    };
  }
};

/**
 * Check if surveys are available for end users
 * @param {number} entityId - Entity ID
 * @returns {Promise<boolean>} True if surveys are available for users
 */
export const areSurveysAvailableForUsers = async (entityId) => {
  if (!entityId) {
    return false;
  }

  try {
    const surveys = await surveyService.getAvailableSurveys(entityId);
    return surveys && surveys.length > 0;
  } catch (error) {
    console.error('Error checking survey availability for users:', error);
    return false;
  }
};